// Session AHN: the Cancel-contract modal. The office picks a cancellation reason (the options,
// their clause references and the initial-term judgement all come from the SERVER's
// ContractDetail.cancellation block, so this modal cannot drift from what the cancel route
// enforces), can preview the letter PDF without committing anything, and commits through an
// explicit two-step confirm. The commit creates a Gmail DRAFT with the letter attached; nothing
// is ever sent from here, and the success panel says exactly that.
import { useMemo, useState } from "react";
import { api, ApiError, type ContractDetail } from "../../lib/api";
import { ModalShell, MODAL_ACTIONS, MODAL_ACTION_BTN } from "../../components/ModalShell";
import { ConfirmButton } from "../../components/ConfirmButton";
import { Button, buttonClasses } from "../../components/Button";
import { formatDateLong } from "../../lib/format";

export function CancelContractModal({
  contract,
  onClose,
  onDone,
}: {
  contract: Pick<ContractDetail, "id" | "siteName" | "customerName" | "customerEmail" | "cancellation">;
  onClose: () => void;
  onDone: () => void;
}) {
  const info = contract.cancellation;
  const [reasonKey, setReasonKey] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ to: string; cc: string | null; immediate: boolean; terminationDate: string } | null>(null);

  const selected = useMemo(() => info.reasons.find((r) => r.key === reasonKey) ?? null, [info.reasons, reasonKey]);
  // Standard Clause 5.1 is refused inside the initial term (the server enforces the same rule;
  // the intermediary 5.1 has no initial-term restriction, and its variant never sets this pair).
  const blockedByTerm = info.variant === "standard" && selected?.clauseRef === "5.1" && info.withinInitialTerm;
  const noEmail = !contract.customerEmail;
  const canGenerate = Boolean(selected) && !blockedByTerm && !noEmail && !busy;

  async function generate() {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.contracts.cancel(contract.id, { reason: selected.key, ...(notes.trim() ? { notes: notes.trim() } : {}) });
      setResult({ to: res.to, cc: res.cc, immediate: res.immediate, terminationDate: res.terminationDate });
    } catch (err) {
      // A server refusal carries the server's own carefully hedged words (relay them). Anything
      // else -- a network drop, a proxy error page with an empty message -- can occur AFTER the
      // server fully committed (this is one of the platform's longest single-request writes:
      // Playwright render + Gmail upload + DB writes), so the client must never assert "nothing
      // has been cancelled" about a result it did not see (AHN review fix).
      setError(
        err instanceof ApiError && err.message
          ? err.message
          : "The connection failed, so the result could not be confirmed. Close this window and reload the page to see whether the notice was recorded, and check Gmail Drafts, before trying again.",
      );
    } finally {
      setBusy(false);
    }
  }

  if (result) {
    return (
      <ModalShell ariaLabel="Cancellation notice created" onClose={onDone}>
        <h2 className="mb-2 font-heading text-xl text-ele-strong">Notice generated</h2>
        <div className="space-y-2 text-sm text-ele-body">
          <p>
            The termination letter for <span className="text-ele-strong">{contract.siteName}</span> is attached to a Gmail draft
            addressed to <span className="text-ele-strong">{result.to}</span>
            {result.cc ? <> (cc {result.cc})</> : null}. <span className="text-ele-strong">Nothing has been sent</span>: open Gmail
            Drafts, check the letter, and send it yourself.
          </p>
          {result.immediate ? (
            <p>
              Cover ended <span className="text-ele-strong">{formatDateLong(result.terminationDate)}</span> (immediate termination). Call-outs
              follow the usual after-cover billing from today.
            </p>
          ) : (
            <p>
              The notice period runs until <span className="text-ele-strong">{formatDateLong(result.terminationDate)}</span>. The contract
              stays active until then, exactly as the letter promises, and ends automatically on that date.
            </p>
          )}
        </div>
        <div className={`mt-5 gap-3 ${MODAL_ACTIONS}`}>
          <Button className={MODAL_ACTION_BTN} onClick={onDone}>
            Done
          </Button>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell ariaLabel="Cancel contract" onClose={() => !busy && onClose()}>
      <h2 className="mb-1 font-heading text-xl text-ele-strong">Cancel contract</h2>
      <p className="mb-4 text-sm text-ele-body">
        Issues formal written notice of termination for {contract.siteName}: a branded letter citing the correct clause of the
        agreement, attached to a Gmail draft for {contract.customerName}. You review and send the draft yourself.
      </p>

      <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body" htmlFor="cancel-reason">
        Reason for cancellation
      </label>
      <select
        id="cancel-reason"
        value={reasonKey}
        onChange={(e) => setReasonKey(e.target.value)}
        className="mb-1 w-full rounded-ele border border-ele-purple-accent bg-ele-field-inset px-3 py-2 text-sm text-ele-strong"
      >
        <option value="">Select a reason...</option>
        {info.reasons.map((r) => (
          <option key={r.key} value={r.key}>
            {r.label}
          </option>
        ))}
      </select>

      {blockedByTerm && (
        <p role="alert" className="mb-2 rounded-ele border border-amber-500 bg-amber-500/10 p-2 text-sm text-amber-300">
          {info.initialTermWarning}
        </p>
      )}
      {selected && !blockedByTerm && (
        <p className="mb-2 text-xs text-ele-body">
          {selected.noticeDays === 0
            ? "Termination takes effect immediately: cover ends today and the letter says so."
            : `${selected.noticeDays} days written notice: the letter names the termination date, and the contract stays active (visits and billing continue) until that date.`}
        </p>
      )}
      {noEmail && (
        <p role="alert" className="mb-2 text-sm text-ele-danger">
          This customer has no billing email on record, so the notice cannot be drafted. Add their email on the customer record
          first.
        </p>
      )}

      <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body" htmlFor="cancel-notes">
        Additional notes (internal only)
      </label>
      <textarea
        id="cancel-notes"
        rows={2}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Never shown to the customer. For the office record only."
        className="mb-1 w-full rounded-ele border border-ele-purple-accent bg-ele-field-inset px-3 py-2 text-sm text-ele-strong"
      />
      {contract.customerEmail && (
        <p className="mb-1 text-xs text-ele-body">
          The draft will be addressed to <span className="text-ele-strong">{contract.customerEmail}</span>.
        </p>
      )}

      {error && (
        <p role="alert" className="mt-2 text-sm text-ele-danger">
          {error}
        </p>
      )}

      <div className={`mt-5 gap-3 ${MODAL_ACTIONS}`}>
        <button
          onClick={() => !busy && onClose()}
          className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body ${MODAL_ACTION_BTN}`}
        >
          Cancel
        </button>
        <button
          onClick={() => selected && !blockedByTerm && window.open(api.contracts.cancellationLetterHref(contract.id, selected.key), "_blank", "noopener")}
          disabled={!selected || blockedByTerm || busy}
          className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-blue hover:text-ele-blue disabled:opacity-50 ${MODAL_ACTION_BTN}`}
        >
          Preview letter
        </button>
        <ConfirmButton
          onConfirm={() => void generate()}
          busy={busy}
          busyLabel="Generating..."
          disabled={!canGenerate}
          label="Generate letter and draft email"
          confirmLabel="Yes, cancel this contract"
          className={buttonClasses("destructive", "sm", MODAL_ACTION_BTN)}
          armedClassName="font-semibold ring-2 ring-ele-danger"
        />
      </div>
    </ModalShell>
  );
}
